import type {
  DevControl,
  DevPreview,
  DevPreviewGroupInfo,
} from "@/modules/dev/types/DevPreview";

import {
  DEFAULT_SURVEY_LOADING_SCENARIO,
  SURVEY_LOADING_SCENARIOS,
} from "@/modules/session-survey/dev/SurveyLoadingPreview/surveyLoadingScenarios";

import { controlsToSearchSchema } from "./shellSearch";

export const DEV_PREVIEW_GROUPS: readonly DevPreviewGroupInfo[] = [
  { id: "booking", title: "Booking" },
  { id: "join", title: "Session join" },
  { id: "survey", title: "Session survey" },
  { id: "routes", title: "Real routes" },
];

// Literal, so each route's search schema is typed by its own controls. Routes
// stay plain objects here: `linkOptions` would type them through the router,
// whose route types read these schemas. DEV_PREVIEWS checks them instead.
const PREVIEWS = [
  {
    id: "booking-notes",
    title: "Booking notes",
    group: "booking",
    description:
      "Standard and coach-matching notes headers at mobile widths, inside the embedded booking layout.",
    ticket: "MEM-1149",
    cycle: 19,
    route: { to: "/dev/booking-notes" },
    layout: "embed",
    fixture: "2026-09-10 15:00Z · Eastern Time",
    controls: [
      {
        key: "standard",
        label: "Show standard booking",
        kind: "boolean",
        default: false,
      },
      {
        key: "compact",
        label: "Compact widget",
        kind: "boolean",
        default: false,
      },
    ],
  },
  {
    id: "booking-end-states",
    title: "Booking end states",
    group: "booking",
    description:
      "Confirmation and error panels with local fixture data, coach and coach-matching variants.",
    ticket: "MEM-1314",
    cycle: 18,
    route: { to: "/dev/booking-end-states" },
    layout: "standard",
    fixture: "Alex Morgan · 2026-08-20 15:00Z ET",
    controls: [
      {
        key: "view",
        label: "View",
        kind: "enum",
        default: "all",
        options: [
          { value: "all", label: "All four" },
          { value: "coach", label: "Confirmation" },
          { value: "coach-matching", label: "Coach matching" },
          { value: "error", label: "Error" },
          { value: "no-coach-free", label: "No coach free" },
        ],
      },
      {
        key: "compact",
        label: "Compact widget",
        kind: "boolean",
        default: false,
      },
    ],
  },
  {
    id: "availability-failure",
    title: "Availability failure",
    group: "booking",
    description:
      "First and repeat failure panels when the availability request times out. Retrying always fails, so the panel escalates.",
    ticket: "MEM-1023",
    cycle: 20,
    route: { to: "/dev/availability-failure" },
    layout: "standard",
    chip: "Interactive",
    controls: [
      { key: "retry", label: "Try again", kind: "action" },
      { key: "reset", label: "Reset", kind: "action" },
    ],
  },
  {
    id: "booking-timezone",
    title: "Booking timezone",
    group: "booking",
    description:
      "The widget preselects the browser's own timezone and adds it when the list lacks it. Change the browser's timezone to try Phoenix, Boise or US/Mountain.",
    ticket: "MEM-1443",
    cycle: 22,
    route: { to: "/dev/booking-timezone" },
    layout: "standard",
    chip: "Interactive",
    controls: [],
  },
  {
    id: "join-loading",
    title: "Join connection recovery",
    group: "join",
    description:
      "The join screen when the network fails: the unavailable panel, then Try again once the fixture connection is restored.",
    ticket: "MEM-1477",
    cycle: 23,
    route: { to: "/dev/join-loading" },
    layout: "standard",
    fixture: "dev-join · offline until restored",
    chip: "Interactive",
    controls: [
      {
        key: "restore",
        label: "Restore fixture connection",
        labelKey: "coachingSession.join.loadingPreview.restore",
        kind: "action",
      },
    ],
  },
  {
    id: "survey-components",
    title: "Survey components",
    group: "survey",
    description:
      "Header, questions, footer and confirmation variants side by side.",
    cycle: 15,
    route: { to: "/dev/survey-components" },
    layout: "standard",
    chip: "Gallery",
    controls: [],
  },
  {
    id: "survey-flow",
    title: "Survey flow",
    group: "survey",
    description:
      "Full flow with fixture questions, mock submit, fail-once and reset, plus an editor for the current question.",
    ticket: "MEM-830",
    cycle: 16,
    route: { to: "/dev/survey-flow" },
    layout: "survey",
    fixture: "dev-survey-flow · 8 questions",
    chip: "Interactive",
    controls: [
      {
        key: "failNext",
        label: "Fail next submit",
        kind: "boolean",
        default: false,
      },
      { key: "reset", label: "Reset flow", kind: "action" },
    ],
  },
  {
    id: "survey-loading",
    title: "Survey loading",
    group: "survey",
    description:
      "Network failures, automatic retries, recovery and the survey status screens.",
    ticket: "MEM-1159",
    cycle: 22,
    route: { to: "/dev/survey-loading" },
    layout: "survey",
    fixture: "dev-loading · first fixture question",
    chip: `${SURVEY_LOADING_SCENARIOS.length} scenarios`,
    controls: [
      {
        key: "scenario",
        label: "Loading scenario",
        labelKey: "sessionSurvey.loadingPreview.scenario",
        kind: "select",
        default: DEFAULT_SURVEY_LOADING_SCENARIO,
        options: SURVEY_LOADING_SCENARIOS.map((scenario) => ({
          value: scenario,
          label: scenario,
          labelKey:
            `sessionSurvey.loadingPreview.scenarios.${scenario}` as const,
        })),
      },
      {
        key: "reset",
        label: "Reset scenario",
        labelKey: "sessionSurvey.loadingPreview.reset",
        kind: "action",
      },
      {
        key: "restore",
        label: "Restore connection",
        labelKey: "sessionSurvey.loadingPreview.restore",
        kind: "action",
      },
    ],
  },
  {
    id: "coach-matching",
    title: "Coach matching (public)",
    group: "routes",
    description:
      "The public Coach Matching booking page with name, email and source prefilled. Needs a reachable bifrost.",
    ticket: "MEM-1364",
    cycle: 22,
    route: {
      to: "/coach-matching",
      search: { name: "Jane", email: "nobody@example.com", source: "email" },
    },
    layout: "standard",
    chip: "Real route",
    controls: [],
    external: true,
  },
  {
    id: "reset-password",
    title: "Reset password",
    group: "routes",
    description:
      "The password reset form from the desktop link fallback, with a placeholder user and token.",
    cycle: 23,
    route: {
      to: "/reset-password",
      search: { user_id: 1, token: "dev-preview" },
    },
    layout: "standard",
    chip: "Real route",
    controls: [],
    external: true,
  },
] as const;

/**
 * Every `/dev` preview and the real routes worth demoing. The rail, search,
 * index cards, stage header and controls drawer all read this list.
 */
export const DEV_PREVIEWS: readonly DevPreview[] = PREVIEWS;

type DevPreviewEntry = (typeof PREVIEWS)[number];

export type DevPreviewId = DevPreviewEntry["id"];

export const getDevPreview = (id: string) =>
  DEV_PREVIEWS.find((preview) => preview.id === id);

/** The `validateSearch` schema of a preview's route, from its controls. */
export const devSearchSchemaFor = <Id extends DevPreviewId>(id: Id) =>
  controlsToSearchSchema(
    (getDevPreview(id)?.controls ?? []) as Extract<
      DevPreviewEntry,
      { id: Id }
    >["controls"],
  );

export const getPreviewsInGroup = (group: DevPreview["group"]) =>
  DEV_PREVIEWS.filter((preview) => preview.group === group);

export const getGroupTitle = (preview: DevPreview) =>
  DEV_PREVIEW_GROUPS.find((group) => group.id === preview.group)?.title;

/** Controls that hold state in the URL, as opposed to one-off actions. */
export const getStateControls = (preview: DevPreview): readonly DevControl[] =>
  preview.controls.filter((control) => control.kind !== "action");

/** State controls that pick one of several values: enums and selects. */
export const getValueControls = (preview: DevPreview): readonly DevControl[] =>
  preview.controls.filter(
    (control) => control.kind === "enum" || control.kind === "select",
  );

export const getPreviewChip = (preview: DevPreview) => {
  if (preview.chip) return preview.chip;

  const count = getStateControls(preview).length;

  return count === 1 ? "1 control" : `${count} controls`;
};
